import {
  ChatBotAction,
  ChatBotDeepLinkAction,
  ChatContentResult,
  ChatContentResultTypenames,
  ChatEventType,
  ChatSender,
  CustomerChatMessage,
  IActionTypenames,
} from '@varo/graphql-schema-types';
import ChatBubble from 'features/chat/ChatBubble/ChatBubble';
import {
  formatChatHTMLMessage,
  getHyperlinkAction,
} from 'features/chat/helpers/Chat.helpers';
import { getHistory } from 'features/chat/helpers/ChatEvent.helper';
import { useSendMessage } from 'features/chat/hooks/useSendMessage';
import { ChatContext } from 'features/chat/providers/ChatContextProvider';
import React, { useCallback, useContext, useEffect } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import TextNode, {
  MixedStyleDeclaration,
  TDefaultRendererProps,
  TNode,
} from 'react-native-render-html';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import useDebounce from 'shared/hooks/useDebounce/useDebounce';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { Maybe } from 'shared/utils/types';

import AnimatedBotOption from './AnimatedBotOption';

interface BotContentProps {
  content: Maybe<ChatContentResult>;
  isTyping: boolean;
  position: 'right' | 'left';
  createdAt: string;
  isDisabled?: boolean;
  index?: number;
}

interface TextNode {
  type: 'text';
  data: string;
}

const BotContent = ({
  content,
  createdAt,
  position,
  isTyping,
  isDisabled,
  index = 0,
}: BotContentProps) => {
  const { setHistory, setIsAgentTyping, chatSessionId } =
    useContext(ChatContext);
  const { isFeatureEnabled } = useStatsig();
  const isDecagonChatEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_DECAGON_CHAT,
  );

  const { sendCustomerMessage } = useSendMessage();

  const { width } = useWindowDimensions();

  const htmlAsString = formatChatHTMLMessage(
    content?.__typename === ChatContentResultTypenames.ChatContentHtml
      ? content.html.trim().replace(/\\"/g, '"')
      : '',
  );

  const opacity = useSharedValue(0);
  const translateY = useSharedValue(20);

  useEffect(() => {
    opacity.value = withDelay(index * 100, withTiming(1, { duration: 300 }));
    translateY.value = withDelay(index * 100, withTiming(0, { duration: 300 }));
  });

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      transform: [{ translateY: translateY.value }],
    };
  });

  // Memoize the callback to prevent useDebounce from recreating timers on every render
  const buttonClickCallback = useCallback(
    async (button: ChatBotAction | ChatBotDeepLinkAction) => {
      if (button.__typename === IActionTypenames.ChatBotAction) {
        const messageEvent: CustomerChatMessage = {
          id: getUuid(),
          sender: ChatSender.User,
          createdAt: new Date().toJSON(),
          eventType: ChatEventType.Message,
          content: {
            message: button.value,
            __typename: ChatContentResultTypenames.ChatContentMessage,
          },
          sessionId: chatSessionId,
        };

        setHistory((history) => getHistory(history, messageEvent));
        setIsAgentTyping(true);

        /* Chat Sender type should be lexbot when user clicks on bot options
      In order to track if customers are manually typing or clicking bot options
      See JT-95288 for ref */
        await sendCustomerMessage(
          button.value,
          ChatSender.LexBot,
          isDecagonChatEnabled,
        );
      }
    },
    [
      chatSessionId,
      isDecagonChatEnabled,
      setHistory,
      setIsAgentTyping,
      sendCustomerMessage,
    ],
  );

  const handleButtonClick = useDebounce(buttonClickCallback, 500);

  const contentTextColor = isDisabled
    ? theme.color.text.primary
    : theme.color.text.inverse;

  const baseTags = ['li', 'ol', 'p', 'ul'];
  const dynamicHtmlStyles: Record<string, MixedStyleDeclaration> =
    baseTags.reduce((acc, tag) => {
      acc[tag] = {
        ...theme.typography.body.base,
        ...(tag === 'p'
          ? {
              marginTop: 0,
              marginBottom: theme.spacing.m,
              width: '100%',
            }
          : {}),
        color: contentTextColor,
        height: 'auto',
      };
      return acc;
    }, {});

  // eslint-disable-next-line no-restricted-syntax -- depends on contentTextColor; follow-up: move to a getStyles(color) function per react-native-styling.md Dynamic Styles
  const dynamicHyperlinkStyle = StyleSheet.create({
    a: {
      ...theme.typography.hyperlink.base,
      color: contentTextColor,
      height: 'auto',
      textDecorationColor: contentTextColor,
    },
  });

  const customRenderer = {
    a: (props: TDefaultRendererProps<TNode>) => {
      const { tnode } = props;
      const url = tnode.attributes.href;
      return (
        <HyperlinkBaseText
          onPress={() => getHyperlinkAction(url)}
          style={dynamicHyperlinkStyle.a}
        >
          {tnode.domNode?.children.map(
            (child) => ((child as unknown as TextNode)?.data as string) || '',
          )}
        </HyperlinkBaseText>
      );
    },
  };

  if (content === null) {
    return null;
  }
  if (content?.__typename === ChatContentResultTypenames.ChatContentMessage) {
    return (
      <Animated.View style={index !== 0 ? null : animatedStyle}>
        <ChatBubble
          isTyping={isTyping}
          position={position}
          createdAt={createdAt}
          isDisabled={isDisabled}
        >
          {content.message}
        </ChatBubble>
      </Animated.View>
    );
  }
  if (content?.__typename === ChatContentResultTypenames.ChatContentHtml) {
    return (
      <Animated.View style={index !== 0 ? null : animatedStyle}>
        <ChatBubble
          isTyping={isTyping}
          position={position}
          createdAt={createdAt}
          isDisabled={isDisabled}
          htmlAsString={htmlAsString}
        >
          <HTMLRenderer
            htmlAsString={htmlAsString}
            tagsStyles={dynamicHtmlStyles}
            contentWidth={width}
            renderers={customRenderer}
            defaultTextProps={{
              style: { ...theme.typography.body.base, color: contentTextColor },
            }}
          />
        </ChatBubble>
      </Animated.View>
    );
  }

  if (
    content?.__typename === ChatContentResultTypenames.ChatContentLexBotOption
  ) {
    const actionsArray = content.action;

    return (
      <View>
        {actionsArray.map((option, index) => (
          <AnimatedBotOption
            key={option.id}
            option={option}
            index={index}
            isDisabled={!!isDisabled}
            onPress={(selectedOption) => {
              void handleButtonClick(selectedOption);
            }}
          />
        ))}
      </View>
    );
  }
};

export default BotContent;
